import { Component, inject, signal, ChangeDetectionStrategy, viewChild, ElementRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { catchError, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Observable, of, Subject, throwError } from 'rxjs';
import { DocumentTemplateService } from '../../services/document-template.service';
import { DocumentTemplateAddDTO, DocumentTemplateField, DocumentTemplateWithPath } from '../../types/document-template';
import { Field, StructureType } from '../../types/fields';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FieldDialogComponent, FieldDialogData, FieldDialogResult } from '../fields/components/field-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../components/confirm-dialog/confirm-dialog.component';
import { ConfirmableDeactivate } from '../../guards/unsaved-changes.guard';
import { LoadingDialogComponent } from '../../components/loading-dialog/loading-dialog.component';
import { PdfPreviewComponent } from '../../components/pdf-preview/pdf-preview.component';
import { TemplateMetadataFieldsComponent } from '../../components/template-metadata-fields/template-metadata-fields.component';
import { TemplateFieldEditorComponent } from './components/template-field-editor/template-field-editor.component';
import { ResizableContainerComponent } from '../../components/resizable-container/resizable-container.component';
import { AnchorFieldsSidebarComponent } from '../../components/anchor-fields-sidebar/anchor-fields-sidebar.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { isTemplateOutOfSync } from '../../utils/templates.utils';
import { processOcrResult } from '../../utils/extraction.utils';

@Component({
  selector: 'app-document-template-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    MatSnackBarModule,
    MatDialogModule,
    PdfPreviewComponent,
    TemplateMetadataFieldsComponent,
    TemplateFieldEditorComponent,
    ResizableContainerComponent,
    AnchorFieldsSidebarComponent,
    MatTooltip,
    MatSlideToggleModule
  ],
  templateUrl: './document-template-form.component.html',
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentTemplateFormComponent implements ConfirmableDeactivate {
  templateForm!: FormGroup;
  selectedFile: File | null = null;
  /** Set after a successful save so the unsaved-changes guard lets the post-save navigation through. */
  private hasSaved = false;
  filePreviewUrl: SafeResourceUrl | null = null;
  currentStep = signal(1);
  isUploading = signal(false);
  isExtractingFields = signal(false);
  fieldExtractionError = signal<string | null>(null);
  private destroy$ = new Subject<void>();

  // Fields from the selected structure (loaded via FieldsService)
  private structureFields_map: { [fieldId: string]: Field } = {};
  expandedFieldIndex = signal<number | null>(null);
  isSubmitting = signal(false);
  isEditMode = signal(false);
  templateId: string | null = null;
  existingTemplate: DocumentTemplateWithPath | null = null;
  isDragging = signal(false);

  // Anchor extraction functionality
  isExtractingAnchors = signal(false);
  anchorExtractionError = signal<string | null>(null);
  overrideExistingValues = signal(true);
  anchors = signal<Record<string, unknown> | null>(null);

  fieldsContainer = viewChild<ElementRef>('fieldsContainer');


  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private documentTemplateService = inject(DocumentTemplateService);
  private sanitizer = inject(DomSanitizer);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  readonly store = inject(OcrPrimeStore);

  public asFormGroup(control: any): FormGroup {
    return control as FormGroup;
  }

  getHeaderTitle(): string {
    const templateName = this.templateForm.get('metadata.name')?.value;
    const nameSuffix = templateName ? ` - ${templateName}` : '';

    if (this.isEditMode()) {
      return this.currentStep() === 1
        ? `Edit Template Details${nameSuffix}`
        : `Edit Template Structure${nameSuffix}`;
    }

    return this.currentStep() === 1
      ? `Document Template Details${nameSuffix}`
      : `Template Structure${nameSuffix}`;
  }

  getHeaderSubtitle(): string {
    if (this.currentStep() === 1) {
      return this.isEditMode()
        ? 'Update the information about your template.'
        : 'Enter the basic information about your template.';
    }
    return 'Define the fields that will be extracted from documents.';
  }

  private scrollToBottom() {
    setTimeout(() => {
      const container = this.fieldsContainer()?.nativeElement;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  }

  constructor() {
    this.templateForm = this.fb.group({
      metadata: this.fb.group({
        name: ['', Validators.required],
        description: ['', Validators.required],
        file: [null, Validators.required],
        fieldsStructureId: ['', Validators.required]
      }),
      structure: this.fb.array([])
    });

    // Re-trigger field extraction when fieldsStructureId changes (if a file is available)
    // In edit mode, only reload field definitions — don't auto-extract fields.
    // Extraction should only happen when the user explicitly clicks "Re-extract".
    this.templateForm.get('metadata.fieldsStructureId')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(newStructureId => {
      if (newStructureId) {
        this.loadFieldDefinitions(newStructureId);
        if (this.selectedFile || (this.isEditMode() && this.existingTemplate?.fileUrl && newStructureId !== this.existingTemplate.fieldsStructureId)) {
          this.extractFields();
        }
      }
    });

    // Check if we're in edit mode by looking for an ID in the route parameters
    this.route.paramMap.pipe(
      takeUntil(this.destroy$),
      switchMap(params => {
        const id = params.get('id');
        if (id) {
          this.isEditMode.set(true);
          this.templateId = id;
          return this.documentTemplateService.getDocumentTemplateById(id);
        }
        return of(null);
      })
    ).subscribe({
      next: (template) => {
        if (template) {
          this.existingTemplate = template;
          if (template.fieldsStructureId) {
            this.loadFieldDefinitions(template.fieldsStructureId);
          }
          this.populateFormWithExistingTemplate(template);

          // Handle highlightField query parameter if present
          const highlightField = this.route.snapshot.queryParamMap.get('highlightField');
          if (highlightField) {
            this.highlightAndExpandField(highlightField);
          }

          // Deep link from the detail page's "Anchor mismatch" banner: auto-run anchor extraction
          if (this.route.snapshot.queryParamMap.get('extractAnchors')) {
            this.extractAnchors();
          }
        }
      },
      error: (error) => {
        console.error('Error loading template for editing:', error);
        this.snackBar.open('Failed to load the template for editing.', 'Close', { duration: 4000 });
        this.router.navigate(['/templates']);
      }
    });
  }

  /**
   * Populates the form with data from an existing template
   */
  private populateFormWithExistingTemplate(template: DocumentTemplateWithPath): void {
    // Update metadata
    this.templateForm.get('metadata')?.patchValue({
      name: template.name,
      description: template.description,
      fieldsStructureId: template.fieldsStructureId || ''
    });

    // Preload existing anchor values so they are displayed and re-sent on save
    this.anchors.set(template.anchors ?? null);

    // Set file preview URL
    this.filePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(template.fileUrl);

    // File is not required in edit mode since it cannot be changed
    this.templateForm.get('metadata.file')?.clearValidators();
    this.templateForm.get('metadata.file')?.updateValueAndValidity();

    // Populate structure fields
    const structureFields = this.templateForm.get('structure') as FormArray;
    structureFields.clear();

    Object.entries(template.structure).forEach(([fieldId, fieldData]) => {
      const fieldDef = this.structureFields_map[fieldId];
      const displayName = fieldDef?.name || fieldId;
      const isComposed = fieldDef && (fieldDef.type === 'object' || fieldDef.type === 'object-list');
      const exampleValue = isComposed
        ? (fieldData.exampleValue ?? null)
        : (fieldData.exampleValue != null ? String(fieldData.exampleValue) : null);

      const fieldGroup = this.fb.group({
        fieldId: [fieldId],
        name: [displayName, [Validators.required]],
        exampleValue: [exampleValue || null],
        description: [fieldData.description || '']
      });
      structureFields.push(fieldGroup);
    });

    // Move to step 2 if there are fields
    if (structureFields.length > 0) {
      this.currentStep.set(2);
    }
  }

  private highlightAndExpandField(fieldName: string): void {
    const fields = this.structureFields.controls;
    const index = fields.findIndex(control => control.get('fieldId')?.value === fieldName);
    if (index !== -1) {
      this.expandedFieldIndex.set(index);
      // Give it a small timeout to ensure the DOM is rendered before scrolling
      setTimeout(() => {
        const element = document.getElementById(`field-editor-${index}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }

  get structureFields(): FormArray {
    return this.templateForm.get('structure') as FormArray;
  }


  // Add the drop method for handling drag-and-drop reordering
  drop(event: CdkDragDrop<string[]> | any): void {
    moveItemInArray(this.structureFields.controls, event.previousIndex, event.currentIndex);
    // update the open field index if the moved item was expanded
    if (this.expandedFieldIndex() !== null && event.previousIndex === this.expandedFieldIndex()) {
      this.expandedFieldIndex.set(event.currentIndex);
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    // Ignore file selection in edit mode
    if (this.isEditMode()) {
      console.log('File selection is disabled in edit mode');
      return;
    }

    const element = event.target as HTMLInputElement;
    const file = element.files?.[0];

    if (file) {
      this.selectedFile = file;
      this.filePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file));
      this.templateForm.get('metadata.name')?.setValue(this.templateForm.get('metadata.name')?.value || file.name);
      this.templateForm.get('metadata.file')?.setErrors(null); // Clear any previous errors
      this.templateForm.get('metadata.file')?.setValue(file);

      // Trigger field extraction
      this.extractFields();
    } else {
      this.selectedFile = null;
      this.filePreviewUrl = null;
      this.templateForm.get('metadata.file')?.setValue(null);
      // Clear fields and suggestions if no file is selected
      this.structureFields.clear();
      this.fieldExtractionError.set(null); // Clear error if file is removed
    }
  }

  // Extracted logic for field extraction to allow retries
  async extractFields(): Promise<void> {
    // In edit mode, we need a file to extract fields from
    if (this.isEditMode() && !this.existingTemplate?.fileUrl) {
      console.log('Cannot extract fields: No file available in edit mode');
      return;
    }

    if (!this.isEditMode() && !this.selectedFile) return;

    // Require a fields structure to be selected before extracting
    const structureId = this.templateForm.get('metadata.fieldsStructureId')?.value;
    if (!structureId) {
      console.log('Cannot extract fields: No fields structure selected');
      return;
    }

    this.isExtractingFields.set(true);
    this.fieldExtractionError.set(null); // Clear previous errors

    // Save existing field data before clearing (keyed by fieldId)
    const existingDescriptions: {[key: string]: string} = {};
    const existingValues: {[key: string]: string} = {};
    this.structureFields.controls.forEach((control: any) => {
      const fId = control.get('fieldId')?.value;
      const desc = control.get('description')?.value;
      const val = control.get('exampleValue')?.value;
      if (fId && desc) {
        existingDescriptions[fId] = desc;
      }
      if (fId && val != null && val !== '') {
        existingValues[fId] = val;
      }
    });

    // Always clear all fields - new extraction replaces old fields
    this.structureFields.clear();

    try {
      let extractionRequest;

      if (this.isEditMode() && this.existingTemplate) {
        // In edit mode, we need to fetch the file content from the URL
        try {
          // Use the existing template's file name and type
          const fileName = this.existingTemplate.name;
          // Assume PDF type for existing templates
          const fileType = 'application/pdf';

          // Use the structureId from the form
          const structureId = this.templateForm.get('metadata.fieldsStructureId')?.value;

          // Get the file URL from the existing template
          const fileUrl = this.existingTemplate.fileUrl;

          // Fetch the file content from the URL
          const response = await fetch(fileUrl);
          const blob = await response.blob();
          const fileContentBase64 = await this.readFileAsBase64(new File([blob], fileName, { type: fileType }));

          // Use the existing extractFieldsFromFile method
          extractionRequest = this.documentTemplateService.extractFieldsFromFile({
            fileName: fileName,
            fileContent: fileContentBase64,
            structureId: structureId
          });
        } catch (error) {
          console.error('Error fetching file content from URL:', error);
          throw new Error('Failed to fetch file content for extraction');
        }
      } else if (this.selectedFile) {
        // In create mode, use the selected file
        const fileContentBase64 = await this.readFileAsBase64(this.selectedFile);
        extractionRequest = this.documentTemplateService.extractFieldsFromFile({
          fileName: this.selectedFile.name,
          fileContent: fileContentBase64,
          structureId: this.templateForm.get('metadata.fieldsStructureId')?.value
        });
      } else {
        throw new Error('No file or template available for extraction');
      }

      const structureId = this.templateForm.get('metadata.fieldsStructureId')?.value;

      extractionRequest.pipe(
        takeUntil(this.destroy$), // Ensure subscription is cleaned up
        tap(response => {
          const extractedEntries = processOcrResult({
            ocrResult: response || {},
            structureFields: structureId ? this.store.getFieldsForStructure(structureId) : [],
            allFieldsById: this.store.fieldsMap(),
            existingDescriptions,
            existingValues,
            overrideExistingValues: this.overrideExistingValues()
          });

          // Populate the form with extracted field entries
          extractedEntries.forEach(entry => {
            const fieldDef = this.structureFields_map[entry.fieldId];
            const fieldGroup = this.fb.group({
              fieldId: [entry.fieldId],
              name: [entry.name, [Validators.required]],
              exampleValue: [entry.exampleValue, fieldDef?.required ? [Validators.required] : []],
              description: [entry.description]
            });
            this.structureFields.push(fieldGroup, { emitEvent: false }); // Push without emitting event initially
          });
          const count = extractedEntries.length;
          // Manually trigger update once after batch adding
          this.structureFields.updateValueAndValidity({ emitEvent: true });
          this.scrollToBottom();
          if (count > 0) {
            this.snackBar.open(`${count} fields extracted from document`, 'Close', { duration: 3000 });
          }
        }),
        catchError((error: any) => {
          console.error('Error calling extract-fields endpoint via service:', error);
          this.fieldExtractionError.set('Failed to extract fields from document. Please add fields manually or try again.');
          this.structureFields.clear(); // Clear fields on error
          return throwError(() => new Error('Failed to extract fields from document.'));
        }),
        finalize(() => {
          this.isExtractingFields.set(false); // Ensure loading state is turned off
        })
      ).subscribe();
    } catch (error) {
        console.error('Error reading file for extraction:', error);
        this.fieldExtractionError.set('Error reading the selected file. Please try again.');
        this.isExtractingFields.set(false);
    }
  }

  // Drag and drop event handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isEditMode()) {
      this.isDragging.set(true);
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    // Ignore file drop in edit mode
    if (this.isEditMode()) {
      console.log('File drop is disabled in edit mode');
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Create a synthetic event to reuse the existing onFileSelected logic
      const syntheticEvent = {
        target: {
          files: [file]
        }
      } as unknown as Event;

      this.onFileSelected(syntheticEvent);
    }
  }

  retryFieldExtraction(): void {
    this.extractFields();
  }

  // Method to retrigger field extraction
  retriggerFieldExtraction(): void {
    this.extractFields();
  }

  nextStep(): void {
    if (this.currentStep() !== 1) {
      return;
    }
    const metadata = this.templateForm.get('metadata');
    if (metadata?.valid) {
      this.currentStep.set(2);
    } else {
      metadata?.markAllAsTouched();
      this.snackBar.open('Please complete the required fields before continuing.', 'Close', { duration: 3000 });
    }
  }

  previousStep(): void {
    if (this.currentStep() === 2) {
      this.currentStep.set(1);
    }
  }

  toggleFieldExpansion(index: number): void {
    this.expandedFieldIndex.set(this.expandedFieldIndex() === index ? null : index);
  }

  async onSubmit(): Promise<void> {
    this.templateForm.markAllAsTouched();

    // Validate form based on mode (create mode additionally requires a file)
    if (!this.templateForm.valid || (!this.isEditMode() && !this.selectedFile)) {
      // Send the user back to the step where the problem is visible
      if (this.templateForm.get('metadata')?.invalid) {
        this.currentStep.set(1);
      }
      this.snackBar.open(
        !this.isEditMode() && !this.selectedFile
          ? 'Please upload a sample document before saving.'
          : 'Please complete the required fields before saving.',
        'Close',
        { duration: 4000 }
      );
      return;
    }

    this.isSubmitting.set(true); // Set loading state to true
    this.isUploading.set(true);
    const formValue = this.templateForm.value;
    const structure: { [key: string]: DocumentTemplateField } = {};

    (formValue.structure || []).forEach((field: any) => {
      const key = field.fieldId || field.name;
      if (key) {
        let exampleValue = field.exampleValue;

        // filter out empty strings
        if (typeof exampleValue === 'string' && exampleValue.trim() === '') {
          exampleValue = null;
        }

        structure[key] = {
          exampleValue: exampleValue,
          description: field.description || undefined,
        };
      }
    });

    try {
      if (this.isEditMode() && this.templateId) {
        // Update existing template (file is never changed in edit mode)
        const updateData = {
          name: formValue.metadata.name,
          description: formValue.metadata.description,
          structure: structure,
          fieldsStructureId: formValue.metadata.fieldsStructureId,
          anchors: this.anchors() ?? undefined
        };

        await this.documentTemplateService.updateDocumentTemplate(this.templateId, updateData).toPromise();
        console.log('Document template updated successfully!');
        this.snackBar.open('Template updated successfully!', 'Close', { duration: 3000 });
      } else {
        // Create new template
        if (!this.selectedFile) {
          throw new Error('File is required for new templates');
        }

        const fileContentBase64 = await this.readFileAsBase64(this.selectedFile);

        const templateData: DocumentTemplateAddDTO = {
          name: formValue.metadata.name,
          description: formValue.metadata.description,
          contentType: this.selectedFile.type,
          structure: structure,
          fileName: this.selectedFile.name,
          fileContent: fileContentBase64,
          fieldsStructureId: formValue.metadata.fieldsStructureId,
          anchors: this.anchors() ?? undefined
        };

        await this.documentTemplateService.createDocumentTemplate(templateData).toPromise();
        console.log('Document template and file submitted successfully!');
        this.snackBar.open('Template created successfully!', 'Close', { duration: 3000 });
      }

      this.hasSaved = true;
      this.router.navigate(['/templates']);
    } catch (error) {
      console.error('Error submitting document template:', error);
      this.snackBar.open('Error saving template. Please try again.', 'Close', { duration: 4000 });
    } finally {
      this.isSubmitting.set(false); // Reset loading state
      this.isUploading.set(false);
    }
  }

  cancel(): void {
    // The unsaved-changes route guard asks for confirmation if the form is dirty.
    this.router.navigate(['/templates']);
  }

  /**
   * True while the form holds work the user would lose by navigating away:
   * any edited control, or an uploaded sample document in create mode.
   */
  private hasUnsavedChanges(): boolean {
    return !this.hasSaved && (this.templateForm.dirty || this.selectedFile !== null);
  }

  /** Called by `unsavedChangesGuard` on any navigation away from this form. */
  canDeactivate(): boolean | Observable<boolean> {
    if (!this.hasUnsavedChanges()) {
      return true;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      panelClass: 'field-dialog-panel',
      data: {
        title: 'Discard unsaved changes?',
        message: 'Your changes to this template have not been saved and will be lost.',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing'
      } as ConfirmDialogData
    });
    return dialogRef.afterClosed().pipe(map(confirmed => !!confirmed));
  }

  async extractAnchors(): Promise<void> {
    console.warn('extractAnchors() is no longer supported in the template form — assign the template to a leaf instead.');
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Ensure result is a string and contains the base64 prefix
        if (typeof result === 'string' && result.includes(',')) {
          const base64String = result.split(',')[1]; // Get the part after the comma
          resolve(base64String);
        } else {
          // Handle cases where result might be null or not a data URL
          reject(new Error('Failed to read file as base64 data URL.'));
        }
      };
      reader.onerror = (error) => reject(error); // Handle file reading errors
      reader.readAsDataURL(file); // Read the file as Data URL
    });
  }
  /**
   * Load field definitions for the selected structure
   */
  private loadFieldDefinitions(structureId: string): void {
    // Ensure store data is loaded
    this.store.loadFields();
    this.store.loadStructures();
    const fields = this.store.getFieldsForStructure(structureId);
    this.structureFields_map = {};
    fields.forEach(f => this.structureFields_map[f.id] = f);
  }

  /**
   * Get the field definition for a given field ID
   */
  getFieldDefinition(fieldId: string): Field {
    return this.structureFields_map[fieldId];
  }

  /**
   * Handle inline global edit of a field definition from the template form.
   * Warns the user that the change is global, opens FieldDialogComponent, persists
   * the update via FieldsService, then refreshes the global store and the local
   * structureFields_map so the row re-renders with the new metadata.
   */
  onEditFieldDefinition(field: Field): void {
    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      panelClass: 'field-dialog-panel',
      data: {
        title: `Edit field "${field.name}" globally?`,
        message: 'This field definition is shared.',
        consequences: ['Changes will affect every template and structure that uses this field.'],
        confirmLabel: 'Continue',
        cancelLabel: 'Cancel'
      } as ConfirmDialogData
    });

    confirmRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.openFieldDefinitionDialog(field);
    });
  }

  private openFieldDefinitionDialog(field: Field): void {
    const dialogRef = this.dialog.open(FieldDialogComponent, {
      data: { mode: 'edit', field, allFields: this.store.fields() } as FieldDialogData,
      width: '95vw',
      panelClass: 'field-dialog-panel'
    });

    dialogRef.afterClosed().subscribe((result: FieldDialogResult | undefined) => {
      if (!result || result.action === 'cancel') return;
      if (!result.fieldId || !result.data) return;

      const loadingRef = this.dialog.open(LoadingDialogComponent, {
        data: { message: 'Updating field…' },
        disableClose: true,
        panelClass: 'loading-dialog-panel'
      });

      this.store.updateField(result.fieldId, result.data).subscribe({
        next: () => {
          const structureId = this.templateForm.get('metadata.fieldsStructureId')?.value;
          if (structureId) {
            this.loadFieldDefinitions(structureId);
          }
          loadingRef.close();
          this.snackBar.open('Field updated successfully', 'Close', { duration: 2000 });
        },
        error: (err) => {
          console.error('Error updating field:', err);
          loadingRef.close();
          this.snackBar.open('Error updating field', 'Close', { duration: 3000 });
        }
      });
    });
  }
}
